import React, { useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { formatShortDateTime } from '../../utils/dateFormatter';
import { formatCount } from '../../utils/numberFormatter';
import { formatSAR } from '../../utils/currencyFormatter';
import { arabicPlural } from '../../utils/alertDisplay';
import {
  useComplaintHistory,
  useComplaintPeople,
  useCreateRepairTask,
  useEditComplaint,
  useCloseComplaint,
  useReopenComplaint
} from '../../hooks/useComplaints';
import { ChoiceButton, ErrorLine, useRunner, textDir } from '../guestReviews/shared';
import SuppliesPicker from '../SuppliesPicker';
import {
  CATEGORIES,
  CATEGORY_LABELS,
  SEVERITIES,
  SEVERITY_LABELS,
  WAITING_ON_LABELS,
  PREVENTION_CHANGE_LABELS,
  GUEST_ISSUE_LABELS,
  PREVENTION_STATUS_LABELS,
  CLOSE_OUTCOME_LABELS,
  REPAIR_TYPES,
  REPAIR_TYPE_LABELS,
  TASK_STATUS_LABELS,
  SOURCE_LABELS,
  HISTORY_LABELS,
  TEXT,
  errorText
} from '../../config/complaintLabels';

/**
 * A complaint in three steps (tasks/followup-tasks-only.md Part B): what happened → a task, or «لا تحتاج مهمة»
 * → close with the lesson once its tasks finished — and its history. Which step is live is the SERVER's
 * (`item.step`, `item.closeOutcomes`); this only renders it. Every refusal comes back as a code and is shown in
 * words; after every action the list is refetched. A closed complaint is read-only until it is reopened.
 *
 * Off the screen, data kept: the guest-issue and prevention steps, compensation, the accountable person and
 * «بانتظار» — closing records the first two server side (B4/B5).
 */

const DONE_TASK = ['completed', 'cancelled'];
const userId = (u) => String(u._id || u.id);
const userName = (u) => u.name || u.email || '';
const nightsText = (n) => `${formatCount(n)} ${arabicPlural(n, TEXT.night, TEXT.twoNights, TEXT.nights)}`;
const actorText = (event) => {
  if (event.actorType === 'person') return event.actorName;
  return event.actorType === 'agent' ? TEXT.agent : TEXT.system;
};

// A step's chip: the live one (purple) is the only one asking for something; done is green, later is grey.
const CHIP_CLASS = { live: '', done: 'cp-chip--done', later: 'cp-chip--later' };

const Block = ({ title, chip, tone = 'live', children }) => (
  <section className="gr-block">
    <div className="gr-block-head">
      <h3 className="gr-block-title">{title}</h3>
      {chip && <span className={`cp-chip ${CHIP_CLASS[tone]}`}>{chip}</span>}
    </div>
    {children}
  </section>
);

const ByLine = ({ name, at }) => (name || at ? (
  <div className="gr-details">
    {name && <span>{name}</span>}
    {at && <span>{formatShortDateTime(at)}</span>}
  </div>
) : null);

/** One required line and a confirm button — «لا تحتاج مهمة», closing and reopening all ask the same way. */
const LineForm = ({ value, onChange, label, placeholder, maxLength, busy, onConfirm, confirmText, onCancel, canConfirm = true }) => (
  <div className="gr-edit">
    <Form.Control
      as="textarea"
      rows={2}
      className="touch-input gr-textarea"
      value={value}
      maxLength={maxLength}
      disabled={busy}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={label}
      dir={textDir(value)}
    />
    <div className="gr-actions">
      <Button className="touch-btn gr-primary" onClick={onConfirm} disabled={busy || !canConfirm || !value.trim()}>
        {busy ? <Spinner animation="border" size="sm" /> : confirmText}
      </Button>
      {onCancel && <Button variant="light" className="touch-btn" onClick={onCancel} disabled={busy}>{TEXT.cancel}</Button>}
    </div>
  </div>
);

// ── 1. What happened ────────────────────────────────────────────────────────────────────────────────
const WhatHappened = ({ item, readOnly }) => {
  const edit = useEditComplaint();
  const { error, run } = useRunner(errorText);
  const busy = edit.isPending;
  const [details, setDetails] = useState(null);
  const startDetails = () => setDetails({ category: item.category, severity: item.severity, description: item.description });

  return (
    <Block title={TEXT.stepWhat}>
      {item.sources.map((s, index) => (
        // A source has no id of its own; the list only ever grows, so its position is stable.
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} className="cp-source">
          <div className="gr-details">
            <span>{SOURCE_LABELS[s.kind]}</span>
            {s.addedByName && <span>{s.addedByName}</span>}
            {s.addedAt && <span>{formatShortDateTime(s.addedAt)}</span>}
          </div>
          {s.excerpt
            ? <blockquote className="gr-text" dir="auto">{s.excerpt}</blockquote>
            : <p className="gr-hint">{TEXT.noExcerpt}</p>}
        </div>
      ))}
      {item.moved && <p className="gr-hint">{TEXT.movedNote}</p>}

      {!readOnly && !details && (
        <div className="gr-actions">
          <Button variant="link" className="touch-btn gr-link" onClick={startDetails}>{TEXT.editDetails}</Button>
        </div>
      )}
      {!readOnly && details && (
        <div className="gr-edit">
          <p className="cp-label">{TEXT.category}</p>
          <div className="gr-choices" role="group" aria-label={TEXT.category}>
            {CATEGORIES.map(code => (
              <ChoiceButton key={code} active={details.category === code} disabled={busy} onClick={() => setDetails(d => ({ ...d, category: code }))}>
                {CATEGORY_LABELS[code]}
              </ChoiceButton>
            ))}
          </div>
          <p className="cp-label">{TEXT.severity}</p>
          <div className="gr-choices" role="group" aria-label={TEXT.severity}>
            {SEVERITIES.map(code => (
              <ChoiceButton key={code} active={details.severity === code} disabled={busy} onClick={() => setDetails(d => ({ ...d, severity: code }))}>
                {SEVERITY_LABELS[code]}
              </ChoiceButton>
            ))}
          </div>
          <Form.Control
            as="textarea"
            rows={3}
            className="touch-input gr-textarea"
            value={details.description}
            maxLength={1000}
            disabled={busy}
            onChange={e => setDetails(d => ({ ...d, description: e.target.value }))}
            placeholder={TEXT.descriptionPlaceholder}
            aria-label={TEXT.descriptionPlaceholder}
            dir={textDir(details.description)}
          />
          <div className="gr-actions">
            <Button className="touch-btn gr-primary" onClick={() => run(edit, { id: item.id, changes: details }, () => setDetails(null))} disabled={busy || !details.description.trim()}>{TEXT.save}</Button>
            <Button variant="light" className="touch-btn" onClick={() => setDetails(null)} disabled={busy}>{TEXT.cancel}</Button>
          </div>
        </div>
      )}
      <ErrorLine message={error} />
    </Block>
  );
};

// ── The repair-task form (C13, C20) ─────────────────────────────────────────────────────────────────
const TaskForm = ({ item, defaults, options, people, onDone, onCancel }) => {
  // `defaultDate` remembers the server's day the form opened with: an unchanged date is not sent, so the
  // server picks today itself — a form left open past midnight never books yesterday.
  const today = (defaults && defaults.scheduledDate) || '';
  const supplies = (options && options.supplies) || [];
  const [form, setForm] = useState({ type: 'maintenance', title: '', notes: '', assignedTo: '', scheduledDate: today, defaultDate: today, restockItemIds: [] });
  const mutation = useCreateRepairTask();
  const { error, run } = useRunner(errorText);
  const busy = mutation.isPending;
  const field = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const isRefill = form.type === 'restock';
  // A refill is titled by the system and carries no notes: that task is shared with the crew, so the
  // complaint's own words stay on the complaint (C20-4).
  const canSubmit = isRefill ? form.restockItemIds.length > 0 : form.title.trim();
  const submit = () => run(mutation, {
    id: item.id,
    type: form.type,
    title: isRefill ? undefined : form.title,
    notes: isRefill ? undefined : form.notes,
    restockItemIds: isRefill ? form.restockItemIds : undefined,
    assignedTo: form.assignedTo || undefined,
    scheduledDate: form.scheduledDate && form.scheduledDate !== form.defaultDate ? form.scheduledDate : undefined
  }, (res) => onDone(res && res.data && res.data.merged ? res.data.ticketId : null));

  return (
    <div className="gr-edit">
      <div className="gr-choices" role="group" aria-label={TEXT.taskType}>
        {REPAIR_TYPES
          // A refill ticks from the crew's supplies sheet; with none seeded there is nothing to tick.
          .filter(type => type !== 'restock' || supplies.length > 0)
          .map(type => (
            <ChoiceButton key={type} active={form.type === type} disabled={busy} onClick={() => setForm(f => ({ ...f, type }))}>
              {REPAIR_TYPE_LABELS[type]}
            </ChoiceButton>
          ))}
      </div>
      <p className="gr-hint">{TEXT.taskTypeHint}</p>
      {isRefill ? (
        <>
          <SuppliesPicker
            items={supplies}
            selected={form.restockItemIds}
            disabled={busy}
            label={TEXT.taskSupplies}
            onChange={(restockItemIds) => setForm(f => ({ ...f, restockItemIds }))}
          />
          <p className="gr-hint">{TEXT.taskSuppliesHint}</p>
        </>
      ) : (
        <>
          <Form.Control
            className="touch-input"
            value={form.title}
            maxLength={200}
            disabled={busy}
            onChange={field('title')}
            placeholder={TEXT.taskTitlePlaceholder}
            aria-label={TEXT.taskTitle}
            dir={textDir(form.title)}
          />
          <Form.Control
            as="textarea"
            rows={2}
            className="touch-input gr-textarea"
            value={form.notes}
            maxLength={1000}
            disabled={busy}
            onChange={field('notes')}
            placeholder={TEXT.taskNotesPlaceholder}
            aria-label={TEXT.taskNotesPlaceholder}
            dir={textDir(form.notes)}
          />
        </>
      )}
      <div className="cp-form-row">
        <Form.Group className="cp-field" controlId={`cp-assignee-${item.id}`}>
          <Form.Label>{TEXT.taskAssignee}</Form.Label>
          <Form.Select className="touch-select" value={form.assignedTo} onChange={field('assignedTo')} disabled={busy}>
            <option value="">{TEXT.unassigned}</option>
            {((people && people.assignees) || []).map(u => (
              <option key={userId(u)} value={userId(u)}>{userName(u)}</option>
            ))}
          </Form.Select>
        </Form.Group>
        {/* A business date: the default day comes from the server (API_DATA_FETCHING_PATTERNS "data.defaults"). */}
        <Form.Group className="cp-field" controlId={`cp-date-${item.id}`}>
          <Form.Label>{TEXT.taskDate}</Form.Label>
          <Form.Control
            type="date"
            className="touch-input"
            value={form.scheduledDate}
            min={(defaults && defaults.scheduledDate) || undefined}
            onChange={field('scheduledDate')}
            disabled={busy}
          />
        </Form.Group>
      </div>
      <div className="gr-actions">
        <Button className="touch-btn gr-primary" onClick={submit} disabled={busy || !canSubmit}>
          {busy ? <Spinner animation="border" size="sm" /> : TEXT.createTask}
        </Button>
        <Button variant="light" className="touch-btn" onClick={onCancel} disabled={busy}>{TEXT.cancel}</Button>
      </div>
      <ErrorLine message={error} />
    </div>
  );
};

// ── 2. Action: a task, or «لا تحتاج مهمة» ───────────────────────────────────────────────────────────
const ACTION_CHIP = {
  action: { text: TEXT.actionNeeded, tone: 'live' },
  in_progress: { text: TEXT.actionTaskOpen, tone: 'later' },
  close: { text: TEXT.actionTasksDone, tone: 'done' }
};

const Action = ({ item, defaults, options, people, readOnly }) => {
  // null · 'task' (the repair-task form) · 'no_task' (the one-line reason)
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState('');
  // The refill task a supplies request joined — ops would otherwise look for a new task that was never
  // created (C20-3).
  const [joined, setJoined] = useState(null);
  const close = useCloseComplaint();
  const { error, run } = useRunner(errorText);
  const busy = close.isPending;
  const canSayNoTask = (item.closeOutcomes || []).includes('no_task');
  const chip = ACTION_CHIP[item.step];
  const closeNoTask = () => run(close, { id: item.id, outcome: 'no_task', note: reason }, () => { setMode(null); setReason(''); });

  return (
    <Block title={TEXT.stepAction} chip={chip && chip.text} tone={chip && chip.tone}>
      {item.tasks.length === 0 && !mode && <p className="gr-hint">{readOnly ? TEXT.noTasks : TEXT.actionHint}</p>}
      {item.tasks.length > 0 && (
        <ul className="cp-tasks">
          {item.tasks.map(t => (
            <li key={t.id} className="cp-task">
              <bdi className="gr-code">{t.ticketId}</bdi>
              <span>{REPAIR_TYPE_LABELS[t.type] || t.type}</span>
              <span dir="auto">{t.title}</span>
              <span className={`cp-chip ${DONE_TASK.includes(t.status) ? 'cp-chip--done' : ''}`}>{TASK_STATUS_LABELS[t.status] || t.status}</span>
              {t.assigneeName && <span className="gr-muted">{t.assigneeName}</span>}
            </li>
          ))}
        </ul>
      )}

      {!readOnly && !mode && (
        <div className="gr-actions">
          {item.canAddTask && (
            canSayNoTask
              ? <Button className="touch-btn gr-primary" onClick={() => { setJoined(null); setMode('task'); }}>{TEXT.addTask}</Button>
              : <Button variant="link" className="touch-btn gr-link" onClick={() => { setJoined(null); setMode('task'); }}>{TEXT.addAnotherTask}</Button>
          )}
          {canSayNoTask && (
            <Button variant="outline-secondary" className="touch-btn" onClick={() => setMode('no_task')}>{TEXT.noTaskNeeded}</Button>
          )}
        </div>
      )}

      {!readOnly && mode === 'task' && (
        <TaskForm
          item={item}
          defaults={defaults}
          options={options}
          people={people}
          onDone={(ticketId) => { setMode(null); setJoined(ticketId); }}
          onCancel={() => setMode(null)}
        />
      )}
      {!readOnly && mode === 'no_task' && (
        <LineForm
          value={reason}
          onChange={setReason}
          label={TEXT.noTaskLabel}
          placeholder={TEXT.noTaskPlaceholder}
          maxLength={1000}
          busy={busy}
          onConfirm={closeNoTask}
          confirmText={TEXT.close}
          onCancel={() => setMode(null)}
        />
      )}
      {joined && (
        <p className="gr-hint">{TEXT.taskJoined} <bdi className="gr-code">{joined}</bdi></p>
      )}
      <ErrorLine message={error} />
    </Block>
  );
};

// ── 3. Close: the lesson, once its tasks finished — or reopen ───────────────────────────────────────
// What the close step says before it is reached, by the server's step.
const CLOSE_LATER = {
  action: { chip: TEXT.closeAfterAction, hint: TEXT.closeAfterActionHint },
  in_progress: { chip: TEXT.closeLater, hint: TEXT.closeAfterTasks }
};

const Closing = ({ item }) => {
  const close = useCloseComplaint();
  const reopen = useReopenComplaint();
  const { error, run } = useRunner(errorText);
  const [outcome, setOutcome] = useState(null);
  const [line, setLine] = useState('');
  const [reason, setReason] = useState(null);
  const busy = close.isPending || reopen.isPending;

  if (item.status === 'closed') {
    return (
      <Block title={TEXT.stepClose} chip={CLOSE_OUTCOME_LABELS[item.closeOutcome] || TEXT.closedOn} tone="done">
        {item.prevention && item.prevention.text && <p className="cp-note" dir="auto">{item.prevention.text}</p>}
        <ByLine name={item.closedByName} at={item.closedAt} />
        {reason === null ? (
          <div className="gr-actions">
            <Button variant="outline-secondary" className="touch-btn" onClick={() => setReason('')}>{TEXT.reopen}</Button>
          </div>
        ) : (
          <LineForm
            value={reason}
            onChange={setReason}
            label={TEXT.reopenLabel}
            placeholder={TEXT.reopenPlaceholder}
            maxLength={500}
            busy={busy}
            onConfirm={() => run(reopen, { id: item.id, reason }, () => setReason(null))}
            confirmText={TEXT.confirm}
            onCancel={() => setReason(null)}
          />
        )}
        <ErrorLine message={error} />
      </Block>
    );
  }

  // The lessons the server allows at this step (B5) — never a list of the page's own.
  const lessons = (item.closeOutcomes || []).filter(code => code !== 'no_task');
  const ready = item.step === 'close' && lessons.length > 0;
  const later = ready ? null : CLOSE_LATER[item.step];
  return (
    <Block title={TEXT.stepClose} chip={ready ? TEXT.readyToClose : (later && later.chip)} tone={ready ? 'live' : 'later'}>
      {later && <p className="gr-hint">{later.hint}</p>}
      {ready && (
        <>
          <p className="gr-hint">{TEXT.closeHint}</p>
          <div className="gr-choices" role="group" aria-label={TEXT.stepClose}>
            {lessons.map(code => (
              <ChoiceButton key={code} active={outcome === code} disabled={busy} onClick={() => setOutcome(code)}>
                {CLOSE_OUTCOME_LABELS[code]}
              </ChoiceButton>
            ))}
          </div>
          {!outcome && <p className="gr-hint">{TEXT.closeChooseFirst}</p>}
          <LineForm
            value={line}
            onChange={setLine}
            label={TEXT.closeLineLabel}
            placeholder={TEXT.closePlaceholder}
            maxLength={1000}
            busy={busy}
            canConfirm={Boolean(outcome)}
            onConfirm={() => run(close, { id: item.id, outcome, note: line }, () => { setOutcome(null); setLine(''); })}
            confirmText={TEXT.close}
          />
        </>
      )}
      <ErrorLine message={error} />
    </Block>
  );
};

// ── History (C17) — read when the card is opened ────────────────────────────────────────────────────
const fromTo = (from, to) => `${TEXT.from} ${from || '—'} ${TEXT.to} ${to || '—'}`;

/** What changed, in one line, from the row's `details` — so a reason or a note is never lost from sight. */
function historyNote({ action, details }) {
  const d = details || {};
  switch (action) {
    case 'created':
      return [SOURCE_LABELS[d.source], CATEGORY_LABELS[d.category]].filter(Boolean).join('، ');
    case 'source_added':
      return SOURCE_LABELS[d.source] || '';
    case 'details_changed':
      return [
        d.category && `${TEXT.category}: ${fromTo(CATEGORY_LABELS[d.category.from], CATEGORY_LABELS[d.category.to])}`,
        d.severity && `${TEXT.severity}: ${fromTo(SEVERITY_LABELS[d.severity.from], SEVERITY_LABELS[d.severity.to])}`,
        d.description && TEXT.descriptionChanged
      ].filter(Boolean).join('، ');
    case 'accountable_changed':
      return fromTo(d.from, d.to);
    case 'waiting_on_changed':
      return fromTo(WAITING_ON_LABELS[d.from], WAITING_ON_LABELS[d.to]);
    case 'guest_issue_changed':
      return [GUEST_ISSUE_LABELS[d.to], d.note].filter(Boolean).join(': ');
    case 'prevention_changed':
      return [PREVENTION_STATUS_LABELS[d.to], d.change && PREVENTION_CHANGE_LABELS[d.change], d.text].filter(Boolean).join(': ');
    case 'compensation_changed': {
      const to = d.to || {};
      return [
        to.freeNights > 0 && nightsText(to.freeNights),
        to.amount > 0 && formatSAR(to.amount),
        to.note
      ].filter(Boolean).join('، ') || TEXT.noCompensation;
    }
    case 'task_created':
    case 'task_linked':
      return [d.ticketId, REPAIR_TYPE_LABELS[d.type]].filter(Boolean).join(' ');
    case 'closed':
      // Closed before the three-step card: no outcome, no line. Closed by the system: the task that finished it.
      return [CLOSE_OUTCOME_LABELS[d.outcome], d.note || d.ticketId].filter(Boolean).join(': ');
    case 'reopened':
      return d.reason || '';
    default:
      return '';
  }
}

const History = ({ id }) => {
  const { data, isLoading, isError } = useComplaintHistory(id, true);
  return (
    <Block title={TEXT.historyTitle}>
      {isLoading && <Spinner animation="border" size="sm" />}
      {isError && <p className="gr-error">{TEXT.historyError}</p>}
      {data && (
        <ul className="cp-history">
          {data.map((event, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={index}>
              <span>{HISTORY_LABELS[event.action] || event.action}</span>
              <span className="gr-muted">{actorText(event)}</span>
              <time className="gr-muted">{formatShortDateTime(event.at)}</time>
              {historyNote(event) && <span className="cp-history-note" dir="auto">{historyNote(event)}</span>}
            </li>
          ))}
        </ul>
      )}
    </Block>
  );
};

const ComplaintDetail = ({ item, defaults, options }) => {
  const readOnly = item.status === 'closed';
  const { data: people } = useComplaintPeople(!readOnly);
  return (
    <div className="cp-detail">
      <WhatHappened item={item} readOnly={readOnly} />
      <Action item={item} defaults={defaults} options={options} people={people} readOnly={readOnly} />
      <Closing item={item} />
      <History id={item.id} />
    </div>
  );
};

export default ComplaintDetail;
